import { createSelector } from '@reduxjs/toolkit';
import {
  EquipmentTypes,
  UtilityTypes,
  type IUtilityMergedTag,
  type IMinimizedProfession,
  type IScissorsLiftUtility,
} from '@d-orca/shared/trusstor';
import {
  selectFloors,
  selectActiveEquipment,
  selectSelectedEquipmentTypes,
} from './monitor.base.selectors';

// Live equipment positions only — `staticEquipment` is the project's catalogue
// (used for type filters / lookup) and its `floorId`, when present, is a
// configured default rather than where the unit currently is. Trusstor's
// building view does the same: it renders only `visibleActiveEquipment`
// (`BuildingDetails.tsx` -> `equipment.filter(eq => eq.floorId === ...)`),
// never the static list.

export type EquipmentPresence = {
  /** EquipmentTypes value — drives icon + label per type. */
  type: EquipmentTypes;
  count: number;
};

function aggregateEquipmentByType(
  list: ReadonlyArray<{ type?: string; name?: string }>,
): EquipmentPresence[] {
  const m = new Map<EquipmentTypes, number>();
  for (const e of list) {
    const t = e.type as EquipmentTypes | undefined;
    if (!t) continue;
    m.set(t, (m.get(t) ?? 0) + 1);
  }
  return Array.from(m.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);
}

/** Equipment presence per floor, grouped by type so the slab chip overlay
 *  can render one icon per type. */
export const selectEquipmentByFloorId = createSelector(
  [selectActiveEquipment, selectSelectedEquipmentTypes],
  (activeEq, selectedTypes) => {
    const eqFilter = selectedTypes.length > 0 ? new Set(selectedTypes) : null;
    const groups = new Map<string, Array<{ type?: string }>>();
    for (const e of activeEq) {
      const fid = e.floorId;
      if (!fid) continue;
      if (eqFilter && !eqFilter.has(e.type as EquipmentTypes)) continue;
      const list = groups.get(fid) ?? [];
      list.push(e);
      groups.set(fid, list);
    }
    const out = new Map<string, EquipmentPresence[]>();
    groups.forEach((list, fid) => out.set(fid, aggregateEquipmentByType(list)));
    return out;
  },
);

/** Equipment presence per (floor, area), grouped by type. */
export const selectEquipmentByFloorAndArea = createSelector(
  [selectActiveEquipment, selectSelectedEquipmentTypes],
  (activeEq, selectedTypes) => {
    const eqFilter = selectedTypes.length > 0 ? new Set(selectedTypes) : null;
    const groups = new Map<string, Map<string, Array<{ type?: string }>>>();
    for (const e of activeEq) {
      const fid = e.floorId;
      const aid = e.areaId;
      if (!fid || !aid) continue;
      if (eqFilter && !eqFilter.has(e.type as EquipmentTypes)) continue;
      let inner = groups.get(fid);
      if (!inner) {
        inner = new Map();
        groups.set(fid, inner);
      }
      const list = inner.get(aid) ?? [];
      list.push(e);
      inner.set(aid, list);
    }
    const out = new Map<string, Map<string, EquipmentPresence[]>>();
    groups.forEach((inner, fid) => {
      const m = new Map<string, EquipmentPresence[]>();
      inner.forEach((list, aid) => m.set(aid, aggregateEquipmentByType(list)));
      out.set(fid, m);
    });
    return out;
  },
);

// Total-count selectors retained for callers that only need a single number
// (Special Areas card). Derived from the per-type selectors above so we
// don't walk activeEquipment twice.
export const selectEquipmentCountByFloorId = createSelector(
  [selectEquipmentByFloorId],
  (byFloor) => {
    const m = new Map<string, number>();
    byFloor.forEach((presences, fid) => {
      m.set(fid, presences.reduce((sum, p) => sum + p.count, 0));
    });
    return m;
  },
);

export const selectEquipmentCountByFloorAndArea = createSelector(
  [selectEquipmentByFloorAndArea],
  (byFloorArea) => {
    const m = new Map<string, Map<string, number>>();
    byFloorArea.forEach((inner, fid) => {
      const innerOut = new Map<string, number>();
      inner.forEach((presences, aid) => {
        innerOut.set(aid, presences.reduce((sum, p) => sum + p.count, 0));
      });
      m.set(fid, innerOut);
    });
    return m;
  },
);

// ---------------------------------------------------------------------------
// Utility (scissor lift / hoist) selectors
//
// Utilities aren't equipment — they're tagged assets carried as
// `IUtilityMergedTag` and grouped per-area on the merged floor view
// (`floor.areas[].utilities`). Trusstor renders them in a dedicated
// `FloorUtilitiesSection` and shows a live indicator when a scissor lift
// is "live" (its `closestEquipment` doesn't contain another scissor lift —
// i.e. it's not parked next to a peer). Mirror that here.
// ---------------------------------------------------------------------------

export type UtilityPresence = {
  /** Tag id — stable React key. */
  id: string;
  type: UtilityTypes;
  /** Display name: scissor lift uses `linkedUtility.name`, hoist uses tagNick. */
  name: string;
  /** Only meaningful for scissor lifts. */
  isLive: boolean;
};

/** Per-type utility chip — one entry per UtilityTypes value present, with
 *  total count and a `hasLive` flag (true if at least one underlying
 *  scissor-lift presence is live). The slab + area chip overlays render
 *  one chip per entry so hoists get a hoist icon and scissor lifts get
 *  the scissor-lift icon — the count is no longer mixed across types. */
export type UtilityTypeChip = {
  type: UtilityTypes;
  count: number;
  hasLive: boolean;
};

export function aggregateUtilitiesByType(
  presences: ReadonlyArray<UtilityPresence>,
): UtilityTypeChip[] {
  const m = new Map<UtilityTypes, UtilityTypeChip>();
  for (const p of presences) {
    const existing = m.get(p.type);
    if (existing) {
      existing.count += 1;
      existing.hasLive = existing.hasLive || p.isLive;
    } else {
      m.set(p.type, { type: p.type, count: 1, hasLive: p.isLive });
    }
  }
  return Array.from(m.values());
}

function toUtilityPresence(
  tag: IUtilityMergedTag<IMinimizedProfession>,
): UtilityPresence | null {
  // `linkedUtility.type` is typed `UtilityTypes` upstream but `passthrough()`
  // on the zod schema widens the inferred shape to include unknown extras,
  // so TS sees `type` as `{}` here. Narrow once.
  const rawType = tag.linkedUtility?.type as UtilityTypes | undefined;
  if (!rawType) return null;
  let name: string;
  if (rawType === UtilityTypes.scissorsLift) {
    name = (tag.linkedUtility as IScissorsLiftUtility).name ?? tag.tagNick;
  } else {
    name = tag.tagNick;
  }
  // `isScissorLiftLive` (trusstor_dashboard `scissorLift.utils.ts`):
  // a lift is live if no piece in its closestEquipment list is itself a
  // scissor lift.
  const isLive =
    rawType === UtilityTypes.scissorsLift
      ? (tag.closestEquipment ?? []).every((eq) => eq.type !== EquipmentTypes.ScissorsLift)
      : false;
  return { id: tag.tagId, type: rawType, name, isLive };
}

/** Per-floor utility presence — aggregated across the floor's areas. */
export const selectUtilitiesByFloorId = createSelector(
  [selectFloors],
  (floors) => {
    const m = new Map<string, UtilityPresence[]>();
    for (const f of floors) {
      const fid = f.floorId as string;
      const presences: UtilityPresence[] = [];
      // Walk both per-floor and per-area lists; areas are always present in
      // the merged view, the floor-level list is best-effort.
      for (const area of f.areas ?? []) {
        for (const tag of area.utilities ?? []) {
          const p = toUtilityPresence(tag);
          if (p) presences.push(p);
        }
      }
      for (const tag of f.utilities ?? []) {
        const p = toUtilityPresence(tag);
        // Don't double-count if the floor-level list duplicates area entries.
        if (p && !presences.some((x) => x.id === p.id)) presences.push(p);
      }
      if (presences.length > 0) m.set(fid, presences);
    }
    return m;
  },
);

/** Per-(floor, area) utility presence — keyed by lower-cased areaId so the
 *  FloorDrawer's SVG-area lookup (which lower-cases extracted ids) matches. */
export const selectUtilitiesByFloorAndArea = createSelector(
  [selectFloors],
  (floors) => {
    const m = new Map<string, Map<string, UtilityPresence[]>>();
    for (const f of floors) {
      const fid = f.floorId as string;
      const inner = new Map<string, UtilityPresence[]>();
      for (const area of f.areas ?? []) {
        const list: UtilityPresence[] = [];
        for (const tag of area.utilities ?? []) {
          const p = toUtilityPresence(tag);
          if (p) list.push(p);
        }
        if (list.length > 0) inner.set(area.areaId.toLowerCase(), list);
      }
      if (inner.size > 0) m.set(fid, inner);
    }
    return m;
  },
);
